import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { PublicHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { apiClient } from "@/lib/api";
import { TIDS } from "@/lib/testIds";
import { toast } from "sonner";
import { Building2, User, Users, ShieldCheck, CheckCircle2, ChevronDown, ChevronUp, Sparkles, UploadCloud, FileText, X, Loader2 } from "lucide-react";

const LEGAL_ENTITIES = ["LLC", "Sole Proprietorship", "Corporation", "S-Corporation", "Partnership", "Non-Profit"];
const RENT_OWN = ["Own", "Rent", "Mortgage/Owned"];
const INDUSTRIES = [
  "Construction", "Retail", "Restaurant / Hospitality", "Healthcare", "Manufacturing",
  "Transportation / Logistics", "Professional Services", "Technology / SaaS",
  "Auto / Repair", "Wholesale", "E-commerce", "Real Estate", "Beauty / Salon",
  "Firearms / gun dealers", "Other",
];
const USE_OF_FUNDS = [
  "Working capital", "Purchase Inventory", "Buy Equipment", "Expansion",
  "Payroll", "Marketing", "Refinance debt", "Real estate", "Other",
];

const OwnerSection = ({ title, icon: Icon, value, onChange, required = true }) => {
  const upd = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700"><Icon className="h-4 w-4" /></span>
        <h2 className="text-xl font-heading font-medium text-slate-900">{title}</h2>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Full name {required && <span className="text-rose-600">*</span>}</Label>
          <Input value={value.name} onChange={(e) => upd("name", e.target.value)} required={required} />
        </div>
        <div className="space-y-1.5">
          <Label>Email {required && <span className="text-rose-600">*</span>}</Label>
          <Input type="email" value={value.email} onChange={(e) => upd("email", e.target.value)} required={required} />
        </div>
        <div className="space-y-1.5">
          <Label>Mobile phone {required && <span className="text-rose-600">*</span>}</Label>
          <Input type="tel" placeholder="+1 555 123 4567" value={value.mobile} onChange={(e) => upd("mobile", e.target.value)} required={required} />
        </div>
        <div className="space-y-1.5">
          <Label>% of ownership</Label>
          <Input type="number" min="0" max="100" value={value.ownership_pct} onChange={(e) => upd("ownership_pct", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Social Security Number {required && <span className="text-rose-600">*</span>}</Label>
          <Input placeholder="XXX-XX-XXXX" value={value.ssn} onChange={(e) => upd("ssn", e.target.value)} required={required} autoComplete="off" />
          <p className="text-xs text-slate-500 inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Encrypted in transit. Only authorized staff can view.</p>
        </div>
        <div className="space-y-1.5">
          <Label>Date of birth {required && <span className="text-rose-600">*</span>}</Label>
          <Input type="date" value={value.dob} onChange={(e) => upd("dob", e.target.value)} required={required} />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Home address {required && <span className="text-rose-600">*</span>}</Label>
          <Input placeholder="Street, City, State, ZIP" value={value.home_address} onChange={(e) => upd("home_address", e.target.value)} required={required} />
        </div>
        <div className="space-y-1.5">
          <Label>Estimated credit score</Label>
          <Input type="number" min="300" max="850" value={value.credit_score} onChange={(e) => upd("credit_score", e.target.value)} />
        </div>
      </div>
    </div>
  );
};

const emptyOwner = { name: "", email: "", mobile: "", ownership_pct: "", ssn: "", dob: "", home_address: "", credit_score: "" };

export default function Apply() {
  const location = useLocation();
  const navigate = useNavigate();
  const sp = new URLSearchParams(location.search);
  const refCode = (sp.get("ref") || "").trim().toUpperCase();
  const empCode = (sp.get("emp") || "").trim().toUpperCase();

  const [referrer, setReferrer] = useState(null);
  useEffect(() => {
    if (!refCode) return;
    apiClient.get(`/partners/lookup/${encodeURIComponent(refCode)}`).then(r => setReferrer(r.data)).catch(() => setReferrer(null));
  }, [refCode]);

  const [biz, setBiz] = useState({
    business_legal_name: "", legal_entity: "LLC", federal_tax_id: "", business_phone: "",
    business_address: "", start_date: "", rent_or_own: "Own", business_description: "",
    industry: "", website: "", monthly_revenue: "", any_balances: "No",
    requested_funding_amount: "",
  });
  const [useOfFunds, setUseOfFunds] = useState([]);
  const [owner, setOwner] = useState(emptyOwner);
  const [hasSecond, setHasSecond] = useState(false);
  const [owner2, setOwner2] = useState(emptyOwner);
  const [agree, setAgree] = useState(false);
  const [signature, setSignature] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]); // File[] queued for upload
  const [dragOver, setDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const today = useMemo(() => new Date().toLocaleDateString(), []);

  const updBiz = (k, v) => setBiz((b) => ({ ...b, [k]: v }));
  const toggleFund = (f) => setUseOfFunds((s) => s.includes(f) ? s.filter(x => x !== f) : [...s, f]);

  const addFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const incoming = Array.from(fileList);
    const valid = incoming.filter((f) => {
      if (f.size > 15 * 1024 * 1024) {
        toast.error(`${f.name} is over 15 MB and was skipped`);
        return false;
      }
      return true;
    });
    setPendingFiles((prev) => {
      const merged = [...prev, ...valid];
      if (merged.length > 15) {
        toast.error("You can attach up to 15 files here — extras were dropped");
        return merged.slice(0, 15);
      }
      return merged;
    });
  };

  const removeFile = (idx) => setPendingFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) return toast.error("Please agree to the authorization to submit");
    if (!signature.trim()) return toast.error("Please type your signature");
    setSubmitting(true);
    try {
      const payload = {
        business_legal_name: biz.business_legal_name,
        legal_entity: biz.legal_entity,
        federal_tax_id: biz.federal_tax_id,
        business_phone: biz.business_phone,
        business_address: biz.business_address,
        start_date: biz.start_date,
        rent_or_own: biz.rent_or_own,
        business_description: biz.business_description,
        industry: biz.industry,
        website: biz.website,
        monthly_revenue: Number(biz.monthly_revenue || 0),
        use_of_funds: useOfFunds,
        any_balances: biz.any_balances,
        requested_funding_amount: Number(biz.requested_funding_amount || 0),
        owner: {
          ...owner,
          ownership_pct: owner.ownership_pct === "" ? null : Number(owner.ownership_pct),
          credit_score: owner.credit_score === "" ? null : Number(owner.credit_score),
        },
        second_owner: hasSecond ? {
          ...owner2,
          ownership_pct: owner2.ownership_pct === "" ? null : Number(owner2.ownership_pct),
          credit_score: owner2.credit_score === "" ? null : Number(owner2.credit_score),
        } : null,
        agree: true,
        signature_name: signature.trim(),
        referral_code: refCode || undefined,
        employee_code: empCode || undefined,
      };
      const resp = await apiClient.post("/applications", payload);
      const appId = resp?.data?.id;

      if (appId && pendingFiles.length > 0) {
        setUploadProgress({ done: 0, total: pendingFiles.length });
        for (let i = 0; i < pendingFiles.length; i++) {
          const f = pendingFiles[i];
          try {
            const fd = new FormData();
            fd.append("file", f);
            fd.append("kind", "bank_statement");
            await apiClient.post(`/applications/${appId}/files`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (uerr) {
            toast.error(`Failed to upload ${f.name}`);
          }
          setUploadProgress({ done: i + 1, total: pendingFiles.length });
        }
      }

      setDone(true);
      toast.success("Application submitted");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit application");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PublicHeader />
        <div className="mx-auto max-w-2xl px-6 py-20 text-center">
          <span className="inline-flex h-16 w-16 items-center justify-center rounded-3xl bg-emerald-50 text-emerald-700 mx-auto">
            <CheckCircle2 className="h-8 w-8" />
          </span>
          <h1 className="mt-6 text-3xl md:text-4xl font-heading font-medium tracking-tight text-slate-900">
            Application received.
          </h1>
          <p className="mt-4 text-slate-600 text-lg">
            Thank you. A member of our team will reach out within one business day to confirm details and start the funding process.
          </p>
          <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-left max-w-lg mx-auto">
            <div className="text-sm font-semibold text-emerald-800">Track your application anytime</div>
            <p className="mt-1 text-sm text-emerald-900/80">
              Sign in with the mobile number you used on the form to watch your progress in real time and to become a partner once you&apos;re ready.
            </p>
          </div>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Link
              to="/client/login"
              state={{ mobile: owner.mobile }}
              data-testid={TIDS.applyTrackLink}
            >
              <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700">Track my application</Button>
            </Link>
            <Link to="/"><Button variant="outline" className="rounded-full">Back to home</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader />
      {referrer ? (
        <div className="bg-emerald-600 text-white text-center text-sm py-2 px-6">
          You were referred by <span className="font-semibold">{referrer.full_name}</span>
          {referrer.company_name ? ` (${referrer.company_name})` : ""}
        </div>
      ) : null}

      <div className="mx-auto max-w-4xl px-4 md:px-6 py-12">
        <div className="mb-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
            <Sparkles className="h-3 w-3" /> Free, no impact to credit at this stage
          </span>
          <h1 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-heading font-medium tracking-tight text-slate-900 leading-tight">
            Business Funding Application
          </h1>
          <p className="mt-3 text-slate-600 text-lg max-w-2xl">
            Tell us about your business and the capital you need. Most decisions inside 24 hours. Information stays confidential.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-8">
          {/* Business Information */}
          <Card className="rounded-2xl border-slate-200 shadow-sm p-7 md:p-9 space-y-5">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700"><Building2 className="h-4 w-4" /></span>
              <h2 className="text-xl font-heading font-medium text-slate-900">Business Information</h2>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Business legal name <span className="text-rose-600">*</span></Label>
                <Input value={biz.business_legal_name} onChange={(e) => updBiz("business_legal_name", e.target.value)} required />
              </div>
              <div className="space-y-1.5">
                <Label>Legal entity <span className="text-rose-600">*</span></Label>
                <Select value={biz.legal_entity} onValueChange={(v) => updBiz("legal_entity", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{LEGAL_ENTITIES.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Federal Tax ID (EIN) <span className="text-rose-600">*</span></Label>
                <Input placeholder="XX-XXXXXXX" value={biz.federal_tax_id} onChange={(e) => updBiz("federal_tax_id", e.target.value)} required />
              </div>
              <div className="space-y-1.5">
                <Label>Business phone <span className="text-rose-600">*</span></Label>
                <Input type="tel" value={biz.business_phone} onChange={(e) => updBiz("business_phone", e.target.value)} required />
              </div>
              <div className="space-y-1.5">
                <Label>Website <span className="text-slate-400 font-normal text-xs">(optional)</span></Label>
                <Input value={biz.website} onChange={(e) => updBiz("website", e.target.value)} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Business address <span className="text-rose-600">*</span></Label>
                <Input placeholder="Street, City, State, ZIP" value={biz.business_address} onChange={(e) => updBiz("business_address", e.target.value)} required />
              </div>
              <div className="space-y-1.5">
                <Label>Start date <span className="text-rose-600">*</span></Label>
                <Input type="date" value={biz.start_date} onChange={(e) => updBiz("start_date", e.target.value)} required />
              </div>
              <div className="space-y-1.5">
                <Label>Rent or own?</Label>
                <Select value={biz.rent_or_own} onValueChange={(v) => updBiz("rent_or_own", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{RENT_OWN.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Industry <span className="text-rose-600">*</span></Label>
                <Select value={biz.industry} onValueChange={(v) => updBiz("industry", v)}>
                  <SelectTrigger><SelectValue placeholder="Select industry" /></SelectTrigger>
                  <SelectContent>{INDUSTRIES.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Any open balances?</Label>
                <Select value={biz.any_balances} onValueChange={(v) => updBiz("any_balances", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="No">No</SelectItem>
                    <SelectItem value="Yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Business description <span className="text-rose-600">*</span></Label>
                <Textarea rows={2} value={biz.business_description} onChange={(e) => updBiz("business_description", e.target.value)} required />
              </div>
              <div className="space-y-1.5">
                <Label>Avg. monthly revenue (USD) <span className="text-rose-600">*</span></Label>
                <Input type="number" min="0" step="100" value={biz.monthly_revenue} onChange={(e) => updBiz("monthly_revenue", e.target.value)} required />
              </div>
              <div className="space-y-1.5">
                <Label>Requested funding amount (USD) <span className="text-rose-600">*</span></Label>
                <Input type="number" min="0" step="1000" value={biz.requested_funding_amount} onChange={(e) => updBiz("requested_funding_amount", e.target.value)} required />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Use of funds <span className="text-slate-500 text-xs">(select all that apply)</span></Label>
              <div className="flex flex-wrap gap-2">
                {USE_OF_FUNDS.map((f) => {
                  const active = useOfFunds.includes(f);
                  return (
                    <button
                      key={f}
                      type="button"
                      onClick={() => toggleFund(f)}
                      className={`rounded-full px-4 py-1.5 text-sm border transition-all ${
                        active
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-slate-700 border-slate-300 hover:border-blue-400"
                      }`}
                    >
                      {f}
                    </button>
                  );
                })}
              </div>
            </div>
          </Card>

          {/* Owner */}
          <Card className="rounded-2xl border-slate-200 shadow-sm p-7 md:p-9">
            <OwnerSection title="Owner Information" icon={User} value={owner} onChange={setOwner} required />
          </Card>

          {/* 2nd Owner */}
          <Card className="rounded-2xl border-slate-200 shadow-sm p-7 md:p-9 space-y-4">
            <button
              type="button"
              onClick={() => setHasSecond((v) => !v)}
              className="w-full flex items-center justify-between text-left"
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-700"><Users className="h-4 w-4" /></span>
                <div>
                  <h2 className="text-xl font-heading font-medium text-slate-900">Second owner</h2>
                  <p className="text-xs text-slate-500">Optional — add if there&apos;s another owner with 20%+ stake.</p>
                </div>
              </div>
              {hasSecond ? <ChevronUp className="h-5 w-5 text-slate-500" /> : <ChevronDown className="h-5 w-5 text-slate-500" />}
            </button>
            {hasSecond ? (
              <>
                <Separator />
                <OwnerSection title="" icon={Users} value={owner2} onChange={setOwner2} required={false} />
              </>
            ) : null}
          </Card>

          {/* Documents */}
          <Card className="rounded-2xl border-slate-200 shadow-sm p-7 md:p-9 space-y-5">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                <UploadCloud className="h-4 w-4" />
              </span>
              <div>
                <h2 className="text-xl font-heading font-medium text-slate-900">Bank statements & documents</h2>
                <p className="text-xs text-slate-500">
                  Attach your last <strong>3–4 months of business bank statements</strong> (PDF or image). Optional but speeds funding by 2–5 business days.
                </p>
              </div>
            </div>

            <label
              htmlFor="apply-file-input"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                addFiles(e.dataTransfer.files);
              }}
              data-testid={TIDS.applyDropZone}
              className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
                dragOver
                  ? "border-emerald-500 bg-emerald-50"
                  : "border-slate-300 bg-slate-50 hover:border-emerald-400 hover:bg-emerald-50/40"
              }`}
            >
              <UploadCloud className="h-10 w-10 text-slate-400 mb-3" />
              <div className="text-sm font-medium text-slate-800">Drop bank statements here, or click to browse</div>
              <p className="text-xs text-slate-500 mt-1">PDF, JPG, PNG, HEIC · Up to 15 MB per file · Max 15 files</p>
              <input
                id="apply-file-input"
                type="file"
                multiple
                data-testid={TIDS.applyFileInput}
                accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,image/*,application/pdf"
                className="hidden"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
              />
            </label>

            {pendingFiles.length > 0 ? (
              <ul className="space-y-2" data-testid={TIDS.applyFileList}>
                {pendingFiles.map((f, i) => (
                  <li
                    key={`${f.name}-${i}`}
                    className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2"
                  >
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-emerald-50 text-emerald-700 shrink-0">
                      <FileText className="h-4 w-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{f.name}</div>
                      <div className="text-xs text-slate-500">
                        {(f.size / (1024 * 1024)).toFixed(2)} MB · {f.type || "file"}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="p-1.5 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-colors"
                      title="Remove"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}

            {uploadProgress.total > 0 && uploadProgress.done < uploadProgress.total ? (
              <div className="flex items-center gap-2 text-sm text-emerald-700">
                <Loader2 className="h-4 w-4 animate-spin" />
                Uploading {uploadProgress.done + 1} of {uploadProgress.total}…
              </div>
            ) : null}
          </Card>

          {/* Authorization */}
          <Card className="rounded-2xl border-slate-200 shadow-sm p-7 md:p-9 space-y-5">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700"><ShieldCheck className="h-4 w-4" /></span>
              <h2 className="text-xl font-heading font-medium text-slate-900">Verification &amp; Signature</h2>
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700 leading-relaxed space-y-3">
              <p>
                By signing this application, I affirm the accuracy and truthfulness of all provided information. I grant becomelenders.com and its representatives permission to obtain consumer and/or personal, business, and investigative reports for commercial loans, including daily repayment features or future receivables purchases. This authorization extends to accessing information from TransUnion, Experian, Equifax, other credit bureaus, banks, creditors, and third parties. I also authorize the sharing of this application and related information with the involved parties.
              </p>
              <p>
                I consent to the release of information by creditors or financial institutions to becomelenders.com and its representatives. I provide my business cell phone number and email address, consenting to receive correspondence and messages regarding transactions. I understand I may receive text messages, with an anticipated frequency of approximately 10 per month, and acknowledge that consent to receive texts is not a condition of approval. I certify the completeness, truth, and accuracy of all provided information.
              </p>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-1" />
              <span className="text-sm text-slate-700">I have read and agree to the authorization above. <span className="text-rose-600">*</span></span>
            </label>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Signature (type your full name) <span className="text-rose-600">*</span></Label>
                <Input
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder="Type to sign"
                  className="font-heading text-lg italic"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input value={today} readOnly className="bg-slate-50" />
              </div>
            </div>
          </Card>

          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
            <p className="text-xs text-slate-500 max-w-md">
              Submitting does <span className="font-medium text-slate-700">not</span> impact your personal credit. A team member contacts you before any hard pull.
            </p>
            <Button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-blue-600 hover:bg-blue-700 h-12 px-7 text-base shadow-sm hover:shadow-md transition-all"
            >
              {submitting ? "Submitting…" : "Submit application"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
